import React from "react";
import { useSearchParams } from "react-router-dom";
import { Dumbbell, Pill } from "lucide-react";
import PageHeader from "../../../components/layout/PageHeader";
import {
  SegmentedControl,
  SegmentedItem,
  SkeletonCard,
  useLoadPhase,
} from "../../../components/ui";
// A deep import rather than the clients barrel: that barrel pulls in the client
// pages, and this screen only needs to know whether there are any clients yet.
import { useClients } from "../../clients/hooks/useClients";
// Deep for the same reason: the billing barrel pulls in the checkout and
// result screens, and this page only needs to know whether the workspace is
// allowed to add anything.
import { useSubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
// And the two other kinds of plan, by their panels alone — each barrel carries
// a builder screen or a dialog behind it that this list never opens itself.
import ClientSupplementsPanel from "../../supplements/components/ClientSupplementsPanel";
import ExercisePlansPanel from "../../exercisePlans/components/ExercisePlansPanel";
import { ReactComponent as PlanIcon } from "../../../assets/svg/sidebar/plan icon.svg";
import MealPlansPanel from "../components/MealPlansPanel";
import { usePlanCounts } from "../hooks/usePlanCounts";

/** The three lists the screen switches between. */
type PlanKind = "meal" | "supplement" | "exercise";

const KINDS: SegmentedItem<PlanKind>[] = [
  { id: "meal", label: "Meal" },
  { id: "supplement", label: "Supplement" },
  { id: "exercise", label: "Exercise" },
];

/** An unknown or missing `?kind=` opens on Meal, rather than on nothing. */
const kindFromParam = (value: string | null): PlanKind =>
  KINDS.some((kind) => kind.id === value) ? (value as PlanKind) : "meal";

interface PlanKindCardProps {
  icon: React.ReactNode;
  /** The tint behind the icon — one per kind, so the three read apart. */
  tint: string;
  /** The server's count of everything of this kind, once it has answered. */
  count: number;
  loading: boolean;
  label: string;
}

/**
 * One of the three totals across the top: how many plans of a kind the
 * workspace holds, whichever list is open under it.
 *
 * The count clears while the call is out rather than holding the last
 * answer: a stale figure standing under a fresh label would read as an answer
 * to a question nobody asked. The card's height is fixed by the icon beside
 * it, so the gap costs nothing.
 */
const PlanKindCard: React.FC<PlanKindCardProps> = ({
  icon,
  tint,
  count,
  loading,
  label,
}) => {
  // A count that arrives quickly goes straight from blank to a figure: the
  // placeholder is only reached once the fetch has visibly stalled.
  const phase = useLoadPhase(loading);

  return (
    <div className="flex items-center gap-4 rounded-md border border-line bg-white p-4 sm:px-6 sm:py-5">
      <span
        className={`flex h-11 w-11 3xl:h-12 3xl:w-12 flex-shrink-0 items-center justify-center rounded-lg ${tint}`}
      >
        {icon}
      </span>
      <div>
        {phase === "ready" ? (
          <p className="text-[22px] 3xl:text-[26px] font-semibold leading-none text-ink">
            {count}
          </p>
        ) : (
          <SkeletonCard
            phase={phase}
            className="h-[22px] w-14 3xl:h-[26px] 3xl:w-16"
          />
        )}
        <p className="mt-1.5 text-[13px] 3xl:text-[14px] text-ink-muted">
          {label}
        </p>
      </div>
    </div>
  );
};

/**
 * The plan list — where the sidebar's Create plan lands.
 *
 * Three lists under one heading, the way a client's Plans tab has them: meal,
 * supplement and exercise, each listing everyone's rather than one person's.
 * The totals across the top count all three at once, whichever list is open.
 *
 * Which list lives in the URL as `?kind=`, for the reason the client tab
 * keeps its own there: the supplement builder is a screen of its own, and
 * closing it, filing a draft or sending a plan all come back here — to the
 * list that opened it, not to the Meal one. Replaced rather than pushed, so
 * Back leaves the screen rather than walking through every list looked at.
 *
 * Only the kind on screen is mounted. Each panel fetches its own page, and
 * three of them mounted at once would do all that work for the two lists
 * nobody is looking at.
 */
const CreatePlanPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const kind = kindFromParam(params.get("kind"));
  const setKind = (next: PlanKind) =>
    setParams({ kind: next }, { replace: true });

  // Every kind of plan is what a subscription pays for, so with none every
  // way into a builder is off. The lists themselves stay readable. Read once
  // here and handed down, so the three panels agree. See `useSubscriptionGate`.
  const gate = useSubscriptionGate();

  // Whether this workspace has anyone to write a plan for. A plan is always a
  // plan *for* someone, so with none there is nothing to offer but the way to
  // add one — which is the Meal list's to say, being the one the screen opens
  // on.
  //
  // The whole list rather than a count: there is no endpoint that answers "how
  // many", and this is the same call My clients makes.
  const { clients, status: clientsStatus } = useClients();

  // Only a list that came back empty counts. While it is out, and if it failed,
  // what this workspace has is unknown — and guessing "none" would hide the
  // Create plan button from someone who has fifty clients.
  const noClientsYet = clientsStatus === "ready" && clients.length === 0;

  // The three totals, in one call — `GET /api/v1/plans/counts/`. A refused
  // read counts as nothing — the same workspace whose lists draw as empty
  // below. See `usePlanCounts`.
  const { counts, status: countsStatus } = usePlanCounts();
  const countsLoading = countsStatus === "loading";

  return (
    // The page owns the viewport height instead of growing past it, so the
    // only thing that scrolls is the table body inside the card.
    <div className="h-full flex flex-col overflow-hidden px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
      <PageHeader title="Create plan" />

      <section
        aria-label="Plan totals"
        className="mt-5 flex-shrink-0 grid grid-cols-1 gap-4 sm:grid-cols-3"
      >
        <PlanKindCard
          icon={<PlanIcon className="h-5 w-5 3xl:h-6 3xl:w-6" />}
          tint="bg-brand-soft"
          count={counts.meal}
          loading={countsLoading}
          label="Total meal plan"
        />
        <PlanKindCard
          icon={
            <Pill
              aria-hidden
              className="h-5 w-5 3xl:h-6 3xl:w-6 text-[#4F6BED]"
            />
          }
          tint="bg-[#EAF0FF]"
          count={counts.supplement}
          loading={countsLoading}
          label="Total supplement plan"
        />
        <PlanKindCard
          icon={
            // A step smaller than the two beside it: a dumbbell is wide for
            // its height, and drawn at the same size it fills the tile where
            // the design leaves it sitting in the middle of one.
            //
            // The design draws it as a 1px stroke. Lucide's stroke is in the
            // units of its 24px viewBox, so 1.5 there is the 1px on screen at
            // the 16px the icon is drawn at — the default 2 reads as bold.
            <Dumbbell
              aria-hidden
              strokeWidth={1}
              className="h-5 w-5 3xl:h-6 3xl:w-6 text-[#8B720E]"
            />
          }
          tint="bg-[#FFFFE7]"
          count={counts.exercise}
          loading={countsLoading}
          label="Total exercise plan"
        />
      </section>

      <SegmentedControl
        items={KINDS}
        value={kind}
        onChange={setKind}
        label="Plan kind"
        className="mt-5 flex-shrink-0"
      />

      <div className="mt-4 flex-1 min-h-0 flex flex-col">
        {kind === "meal" && (
          <MealPlansPanel gate={gate} noClientsYet={noClientsYet} />
        )}

        {kind === "supplement" && <ClientSupplementsPanel gate={gate} />}

        {kind === "exercise" && <ExercisePlansPanel gate={gate} />}
      </div>
    </div>
  );
};

export default CreatePlanPage;
